import { useMutation, useQuery } from '@apollo/client/react';
import { GET_BASIC_CUSTOMER_DATA_QUERY } from '../../integrations/allkort/graphql/gql/CustomerQueries';
import { ADD_REPLACEMENT_CARD_CUSTOMER_TASK_MUTATION } from '../../integrations/allkort/graphql/gql/CustomerTasksMutations';
import { BasicCustomerDataResponse } from '../../integrations/allkort/graphql/interface/Customer';
import {
	AddReplacementCardCustomerTaskInput,
	AddReplacementCardCustomerTaskResponse,
} from '../../integrations/allkort/graphql/interface/CustomerTaskMutation';

/**
 * useAddReplacementCardTask
 *
 * Custom hook for submitting an "replace card" customer task.
 * Fetches the current customer's ID from cache and exposes the mutation.
 *
 * @example
 * ```tsx
 * const { addReplacementCardTask, loading, error } = useAddReplacementCardTask();
 *
 * await addReplacementCardTask({
 *   cardId: 1409815,
 *   disableNow: true,
 *   disableDueDate: null,
 *   hasApprovedCreditCheck: false,
 *   inheritPinCode: true,
 *   pinCode: null,
 *   reference2: 'My Card',
 *   replacementDueDate: new Date().toISOString(),
 * });
 * ```
 */
export const useAddReplacementCardTask = () => {
	const { data: customerData } = useQuery<BasicCustomerDataResponse>(
		GET_BASIC_CUSTOMER_DATA_QUERY,
		{ fetchPolicy: 'cache-first' }
	);

	const [addReplacementCardCustomerTaskMutation, { loading, error, data }] = useMutation<
		AddReplacementCardCustomerTaskResponse,
		AddReplacementCardCustomerTaskInput
	>(ADD_REPLACEMENT_CARD_CUSTOMER_TASK_MUTATION);

	const addReplacementCardTask = async (
		input: Omit<AddReplacementCardCustomerTaskInput, 'customerId'>
	) => {
		const customerId = customerData?.currentCustomer?.id;
		if (!customerId) {
			throw new Error('Customer ID not available');
		}

		const variables: AddReplacementCardCustomerTaskInput = {
			cardId: input.cardId,
			customerId,
			disableDueDate: input.disableDueDate,
			disableNow: input.disableNow,
			hasApprovedCreditCheck: input.hasApprovedCreditCheck,
			inheritPinCode: input.inheritPinCode,
			pinCode: input.pinCode,
			reference1: input.reference1 ?? '',
			reference2: input.reference2,
			replacementDueDate: input.replacementDueDate,
		};

		const result = await addReplacementCardCustomerTaskMutation({ variables });
		if (result.error) {
			console.error('[useAddReplacementCardTask] GraphQL errors:', result.error);
		}
		return result;
	};

	return { addReplacementCardTask, loading, error, data };
};
